import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { getKarkhanaDashboard } from "../../../services/karkhanaApi";
import { hours } from "../punches/punchModel";
import { fmtDay, rupees } from "../rates/rateModel";
import { CLOSE_REASONS } from "../rounds/LoomSheet";
import "./karkhana-dashboard.scss";
import { kp } from "../karkhanaPaths";

type Tab = "looms" | "people";

const FLAG: Record<string, { label: string; tone: "review" | "stale" | "rate" }> = {
  WAITING_APPROVAL: { label: "Waiting for approval", tone: "review" },
  LENGTH_MISMATCH: { label: "Length doesn't match", tone: "review" },
  NOT_UPDATED: { label: "Not updated", tone: "stale" },
  NO_RATE: { label: "No rate card", tone: "rate" },
};

const STATUS: Record<string, string> = { WEAVING: "Weaving", PREPARING: "Being prepared", CLOSED: "Closed", IDLE: "No saree" };

const reason = (k?: string) => CLOSE_REASONS.find((r) => r.key === k)?.label || k || "";

/** Loom row colour: the most urgent flag wins (review, then not updated, then no rate). */
const toneOf = (flags: string[]): string => {
  if (flags.some((f) => FLAG[f]?.tone === "review")) return "review";
  if (flags.includes("NOT_UPDATED")) return "stale";
  if (flags.includes("NO_RATE")) return "rate";
  return "";
};

/**
 * Below the everyday shortcuts: every loom at a glance (who, how far, days, cost, what needs a
 * look) and the people side (who is in today, what they're owed, studio totals).
 */
const KarkhanaDashboard = () => {
  const history = useHistory();
  const [tab, setTab] = useState<Tab>("looms");
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    getKarkhanaDashboard()
      .then((res: any) => setData(res?.success ? res.data : null))
      .catch(() => setData(null));
  }, []);

  const looms: any[] = data?.looms || [];
  const counts = data?.loomCounts || {};
  const attention = looms.filter((l) => (l.flags || []).length).length;
  const money = !!data?.showMoney;
  const people = data?.people || {};
  const pt = people.totals || {};

  return (
    <section className="kd">
      <div className="kd__tabs" role="tablist" aria-label="Dashboard">
        {(["looms", "people"] as Tab[]).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`kd__tab${tab === k ? " is-on" : ""}`} onClick={() => setTab(k)}>
            {k === "looms" ? `Looms${looms.length ? ` · ${looms.length}` : ""}` : `People${pt.presentToday != null ? ` · ${pt.presentToday} in today` : ""}`}
          </button>
        ))}
      </div>

      {!data ? <div className="kw-empty">Loading…</div> : null}

      {data && tab === "looms" ? (
        <>
          <div className="kd__stats">
            <div>
              <span>Weaving</span>
              <strong>{counts.WEAVING || 0}</strong>
            </div>
            <div>
              <span>Being prepared</span>
              <strong>{counts.PREPARING || 0}</strong>
            </div>
            <div>
              <span>Closed · no saree</span>
              <strong>
                {counts.CLOSED || 0} · {counts.IDLE || 0}
              </strong>
            </div>
            <div className={attention ? "is-warn" : ""}>
              <span>Need a look</span>
              <strong>{attention}</strong>
            </div>
            {money ? (
              <div>
                <span>Cost on the looms</span>
                <strong>{rupees(data.costOnLooms, 0)}</strong>
              </div>
            ) : null}
          </div>
          <p className="kd__legend">
            <span className="kd-dot is-review" /> needs review <span className="kd-dot is-stale" /> not updated for 2+ days{" "}
            <span className="kd-dot is-rate" /> no approved rate
          </p>
          <div className="kd-looms">
            <div className="kd-loom is-head">
              <span>Loom</span>
              <span>Saree</span>
              <span>Weaving</span>
              <span>Progress</span>
              <span className="num">Days</span>
              <span className="num">Left</span>
              {money ? <span className="num">Cost</span> : null}
            </div>
            {looms.map((l) => {
              const tone = toneOf(l.flags || []);
              return (
                <button
                  key={l.id}
                  type="button"
                  className={`kd-loom${tone ? ` is-${tone}` : ""}${l.status !== "WEAVING" ? " is-quiet" : ""}`}
                  onClick={() => history.push(kp.rounds({ loom: l.id }))}
                >
                  <span className="kd-loom__code">
                    <strong className="kw-code">{l.code}</strong>
                    {l.loomGroup ? <small>{l.loomGroup}</small> : null}
                  </span>
                  {l.status === "WEAVING" || (l.status === "CLOSED" && l.sareeNumber) ? (
                    <>
                      <span className="kd-loom__saree">
                        <span className="kw-code">{l.sareeNumber}</span> · {l.designNumber}
                        {l.status === "CLOSED" ? <small className="kd-pill">Closed · {reason(l.closedReason)}</small> : null}
                      </span>
                      <span className="kd-loom__who">
                        {l.weaver?.code} {l.weaver?.name}
                        {l.helper ? <small> + {l.helper.code}</small> : <small> · alone</small>}
                      </span>
                      <span className="kd-loom__progress">
                        <span className="kd-bar" aria-hidden="true">
                          <span style={{ transform: `scaleX(${Math.min(1, (l.percent || 0) / 100)})` }} />
                        </span>
                        <small>
                          {l.percent != null ? `${l.percent}%` : "—"} · {l.sareeInches} of {l.fullInches || "?"} in
                        </small>
                      </span>
                      <span className="num" title="Days since the saree started · days with weaving">
                        {l.daysElapsed}d · {l.workingDays} woven
                      </span>
                      <span className="num" title="At this saree's own pace">{l.daysLeft != null ? `~${l.daysLeft}d left` : "—"}</span>
                      {money ? <span className="num">{rupees(l.cost, 0)}</span> : null}
                    </>
                  ) : (
                    <span className="kd-loom__idle">
                      <span className="kd-pill">{STATUS[l.status]}</span>
                      {l.status === "CLOSED" ? ` ${reason(l.closedReason)}` : l.status === "PREPARING" ? " warping / loom setting in the last 2 weeks" : " start a saree in Rounds"}
                    </span>
                  )}
                  {(l.flags || []).length ? (
                    <span className="kd-loom__flags">
                      {(l.flags || []).map((f: string) => (
                        <span key={f} className={`kd-flag is-${FLAG[f]?.tone || "review"}`}>
                          {FLAG[f]?.label || f}
                        </span>
                      ))}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      {data && tab === "people" ? (
        <>
          <div className="kd__stats">
            <div>
              <span>Working with us</span>
              <strong>{pt.workers ?? "—"}</strong>
            </div>
            <div>
              <span>Came today · in now</span>
              <strong>
                {pt.presentToday ?? 0} · {pt.inNow ?? 0}
              </strong>
            </div>
            {money ? (
              <>
                <div className={Number(pt.wagesDue) > 0 ? "is-due" : ""}>
                  <span>Wages due, everyone</span>
                  <strong>{rupees(pt.wagesDue, 0)}</strong>
                </div>
                <div>
                  <span>Advance owed, everyone</span>
                  <strong>{rupees(pt.advanceOutstanding, 0)}</strong>
                </div>
              </>
            ) : null}
          </div>
          <h3 className="kr-group-title">Today</h3>
          {!(people.present || []).length ? (
            <div className="kw-empty">
              Nobody marked in yet. <Link to={kp.attendance({ mode: "arrive" })}>Mark arrivals</Link>
            </div>
          ) : (
            <div className="kd-people">
              {(people.present || []).map((p: any) => (
                <Link key={p.id} to={kp.pay({ person: p.id })} className="kd-person">
                  <span>
                    <strong className="kw-code">{p.code}</strong> {p.name}
                  </span>
                  <span className={`kd-pill${p.stillIn ? " is-in" : ""}`}>
                    {p.stillIn ? `In since ${p.since}` : "Out"}
                  </span>
                  <span className="kw-muted">
                    {p.stillIn ? (p.payBasis === "PIECE" ? "Loom work" : p.activity?.name || "Time work") : ""}
                    {p.timeMinutes ? ` · ${hours(p.timeMinutes)} time` : ""}
                  </span>
                  {money ? (
                    <span className="num">
                      {rupees(p.wagesDue || 0, 0)} due
                      {Number(p.advanceOutstanding) ? <small> · adv {rupees(p.advanceOutstanding, 0)}</small> : null}
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>
          )}
          {money ? (
            <p className="kw-muted mt-2">
              Everyone's balance, including people not in today: <Link to={kp.pay()}>Pay ledger</Link> · as of {fmtDay(data.date)}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export default KarkhanaDashboard;
